/**
 * The hover card for a cytoscape graph.
 *
 * A node label has room for a name and nothing else, so everything that
 * gives the name meaning — the identifier, the amounts, who governed — lives
 * here. One element, moved with the pointer, rather than one per node.
 */

import type cytoscape from 'cytoscape';

export interface TooltipData {
  type: string;
  label: string;
  href?: string;
  details?: Record<string, string | number | null | undefined>;
}

const TYPE_LABELS: Record<string, string> = {
  company: 'Фирма', partner: 'Съизпълнител', award: 'Поръчки в общината',
  municipality: 'Община', person: 'Лице', party: 'Партия', consortium: 'Обединение',
};

export function attachTooltip(instance: cytoscape.Core) {
  const tooltip = document.getElementById('tooltip');
  if (!tooltip) return () => {};

  const show = (event: cytoscape.EventObject) => {
    const data = event.target.data() as TooltipData;
    tooltip.replaceChildren();
    const kind = document.createElement('div');
    kind.className = 'tt-type';
    kind.textContent = TYPE_LABELS[data.type] ?? data.type;
    const name = document.createElement('div');
    name.className = 'tt-name';
    name.textContent = data.label;
    tooltip.append(kind, name);
    for (const [key, value] of Object.entries(data.details ?? {})) {
      if (value == null || value === '') continue;
      const row = document.createElement('div');
      row.className = 'tt-row';
      const term = document.createElement('span');
      term.textContent = `${key}: `;
      row.append(term, document.createTextNode(String(value)));
      tooltip.append(row);
    }
    if (data.href) {
      const hint = document.createElement('div');
      hint.className = 'tt-row muted';
      hint.style.marginTop = '.35rem';
      hint.textContent = 'клик за досие';
      tooltip.append(hint);
    }
    tooltip.style.display = 'block';
  };

  const hide = () => { tooltip.style.display = 'none'; };
  const move = (event: cytoscape.EventObject) => {
    if (tooltip.style.display !== 'block') return;
    const source = event.originalEvent as MouseEvent | undefined;
    if (!source) return;
    /* clamped to the card's real size: a fixed 360px allowance pushed the
       card to a negative left on any screen narrower than it */
    const pad = 8;
    tooltip.style.left = `${Math.max(pad,
      Math.min(source.clientX + 14, window.innerWidth - tooltip.offsetWidth - pad))}px`;
    tooltip.style.top = `${Math.max(pad,
      Math.min(source.clientY + 14, window.innerHeight - tooltip.offsetHeight - pad))}px`;
  };

  instance.on('mouseover', 'node', show);
  instance.on('mouseout', 'node', hide);
  instance.on('mousemove', move);
  return hide;
}
